import type { TFunction } from "i18next"

import { classifyLot } from "./quality"

// Form state for the fabrication dialog. `netMt` is the value as the
// operator types it (MT for readability); we convert to kg only at
// submit time. `lotId` is the operator-supplied identifier, pre-filled
// from /lots/next-number — only required for FULL outcomes; QUEUE
// outcomes get a server-minted QDL-… number.
export interface FabricationDraft {
  actualBags: number
  netMt: number
  lotId: string
}

// validateFabrication is the strict pre-submit check for the dialog.
// Mirrors the backend: bags must be positive and within the plan's
// own bag count (not a global 385 ceiling — operators may plan
// columns smaller or larger than a full lot), netKg > 0, and lotId
// required only when the live classification is FULL.
export function validateFabrication(
  draft: FabricationDraft,
  plannedBags: number,
  t: TFunction<"milling">,
): string[] {
  const errors: string[] = []
  const bags = Number(draft.actualBags) || 0
  const netMt = Number(draft.netMt) || 0
  const trimmedLotId = draft.lotId.trim()

  if (bags <= 0) {
    errors.push(t("fabricate.errors.bagsPositive"))
  } else if (plannedBags > 0 && bags > plannedBags) {
    errors.push(t("fabricate.errors.bagsAbovePlanned", { max: plannedBags }))
  }
  if (netMt <= 0) {
    errors.push(t("fabricate.errors.weightPositive"))
  }
  // QUEUE outcomes get a server-minted QDL- number, so an empty
  // lotId is fine then. FULL outcomes still need an operator id.
  const willBeFull = classifyLot(bags, netMt * 1000) === "FULL"
  if (willBeFull && !trimmedLotId) {
    errors.push(t("fabricate.errors.lotIdRequired"))
  }
  return errors
}
